import { Component, inject } from '@angular/core';
import { UtilityToolbarComponent } from '../../shared-components/utility-toolbar/utility-toolbar.component';
import { NgxLoadingModule } from 'ngx-loading';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule, NgForm } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import * as Highcharts from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { MatInputModule } from '@angular/material/input';
import { MaskService } from '../../services/mask.service';
import { MaskitoDirective } from '@maskito/angular';
import { RightBridgeApiService } from '../../services/right-bridge-api.service';
import { AnnuityWizardService } from '../services/annuity-wizard-service.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { finalize, map, Observable, of, switchMap } from 'rxjs';
import { CannexResponse } from '../../models/global.model';
import { MatFormFieldModule } from '@angular/material/form-field';

interface IndexData {
  name: string;
  average: number;
  best: number;
  worst: number;
  median: number;
  avgPositive: number;
  avgNegative: number;
}

@Component({
  selector: 'app-cannex-calc',
  imports: [
    UtilityToolbarComponent,
    NgxLoadingModule,
    MatSelectModule,
    MatButtonModule,
    FormsModule,
    MatCheckboxModule,
    HighchartsChartComponent,
    MatInputModule,
    MaskitoDirective,
    MatFormFieldModule,
  ],
  templateUrl: './cannex-calc.component.html',
  styleUrl: './cannex-calc.component.scss',
})
export class CannexCalcComponent {
  private rbs = inject(RightBridgeApiService);
  private aws = inject(AnnuityWizardService);
  private ss = inject(SessionStorageService);
  private mask = inject(MaskService);
  formModel = {
    annuityType: '',
    historicalYearsSelect: null,
    initialPremium: null,
    fiaContractLength: null,
    fiaFeeValue: null,
    fiaStrategySelection: '',
    fiaCap: null,
    fiaParticipation: null,
    fiaTrigger: null,
    minimumGuaranteedReturn: 0,
    fiaCalcPeriodSelect: null,
    fiaIndexPick: [],
    rilaContractLength: null,
    rilaFeeValue: null,
    rilaStrategySelection: '',
    rilaCap: null,
    rilaParticipation: null,
    rilaTrigger: null,
    rilaBuffer: null,
    rilaFloor: null,
    rilaCalcPeriodSelect: null,
    rilaIndexPick: [],
  };
  loading = false;
  historicalYears: number[] = [10, 15, 20, 25, 30, 35, 40];
  fiaStrategyList: { name: string; value: string }[] = [
    { name: 'Cap With Participation', value: 'CapWithParticipation' },
    { name: 'Trigger With Participation', value: 'TriggerWithParticipation' },
    { name: 'Participation Only', value: 'ParticipationOnly' },
  ];
  rilaStrategyList: { name: string; value: string }[] = [
    { name: 'Cap With Buffer', value: 'CapWithBuffer' },
    { name: 'Cap With Floor', value: 'CapWithFloor' },
    { name: 'Trigger With Buffer', value: 'TriggerWithBuffer' },
    { name: 'Trigger With Floor', value: 'TriggerWithFloor' },
    { name: 'Participation With Buffer', value: 'ParticipationWithBuffer' },
    { name: 'Participation With Floor', value: 'ParticipationWithFloor' },
  ];
  pickList: string[] = [
    'S&P 500 Index',
    'NASDAQ-100 Index',
    'Russell 2000 Index',
    'S&P 500 Average Daily Risk Control Price Return Index',
    'S&P 500 Low Volatility Index',
    'MSCI EAFE Index',
    'MSCI World Index',
    'Dow Jones Industrial Average',
    'Euro Stoxx 50 Price EUR',
    'Hang Seng Index',
    'Gold Commodity / LBMA Gold Price PM',
    'ML Strategic Balanced Index',
    'Oil WTI Index',
  ];
  numbers20 = Array.from({ length: 19 }, (_, i) => i + 2);
  numbers10 = Array.from({ length: 9 }, (_, i) => i + 2);
  indices: IndexData[] = [];

  dollarMask = this.mask.dollarLimitedMaskSpecs(5000000);
  percentMask = this.mask.percentMaskSpecs();
  over100PercentMask = this.mask.Over100percentMaskSpecs();
  zeroPercent = '0%';
  cannexData: any = null;
  currentYear: number = new Date().getFullYear();
  Highcharts: typeof Highcharts = Highcharts;
  private lineChartRef?: Highcharts.Chart;
  private columnChartRef?: Highcharts.Chart;
  private stackedColumnChartRef?: Highcharts.Chart;
  private requestInFlight = false;
  private refreshPending = false;
  private latestForm!: NgForm;

  lineChartOptions: Highcharts.Options = {
    chart: {
      plotBorderColor: 'var(--highcharts-neutral-color-10, #e6e6e6)',
      plotBorderWidth: 1,
    },

    title: {
      text: '-Year Horizon Growth Graph',
      align: 'left',
    },

    subtitle: {
      text: '-Year Growth',
    },

    yAxis: {
      title: {
        text: 'Portfolio Growth Value(USD)',
      },
    },

    xAxis: {
      title: {
        text: 'Fiscal Year',
      },
      type: 'datetime',
      dateTimeLabelFormats: {
        year: '%Y',
      },
      tickColor: 'var(--highcharts-neutral-color-10, #e6e6e6)',
    },

    legend: {
      enabled: false,
    },

    tooltip: {
      shared: true,
      headerFormat: `<table>
              <caption>{point.x:%Y}</caption>`,
      pointFormat: `
        <tr>
          <th>
            <svg width="20" height="10">
              <path d="M 5 5 L 13 5" stroke="{series.color}" stroke-width="5"
                stroke-linecap="round" />
            </svg>
            {series.name}
          </th>
          <td>{point.y}</td>
        </tr>
        `,
      footerFormat: '</table>',
      valuePrefix: '$',
      useHTML: true,
    },

    // Common options for all series
    plotOptions: {
      series: {
        pointStart: Date.UTC(
          this.currentYear - (this.formModel.historicalYearsSelect ?? 0),
          0,
          1
        ),
        pointIntervalUnit: 'year',
        pointInterval: 1,
        marker: {
          enabled: false,
        },
      },
    },

    exporting: {
      csv: {
        dateFormat: '%Y',
      },
    },
    series: [],
  };

  columnChartOptions: Highcharts.Options = {
    chart: {
      type: 'column',
    },
    title: {
      text: 'Annuity Term Outcomes',
      align: 'left',
    },
    xAxis: {
      categories: [],
      crosshair: true,
      accessibility: {
        description: 'Index names',
      },
    },
    yAxis: {
      min: 0,
      title: {
        text: 'Percentage (%)',
      },
    },
    legend: {
      symbolRadius: 3,
    },
    tooltip: {
      shared: true,
      headerFormat: '<table><caption>{point.key}</caption>',
      pointFormat: `
        <tr>
          <th>
            <svg width="20" height="10">
              <rect x="5" y="0" width="10" height="10" rx="3" ry="3"
                  fill="{series.color}" />
            </svg>
            {series.name}
          </th>
          <td>{point.y}</td>
        </tr>
        `,
      footerFormat: '</table>',
      valueSuffix: ' %',
      useHTML: true,
    },
    plotOptions: {
      column: {
        pointPadding: 0.1,
        borderWidth: 0,
      },
    },
    series: [],
  };

  stackedColumnChartOptions: Highcharts.Options = {
    chart: {
      type: 'column',
    },
    title: {
      text: 'Index Positive Window',
      align: 'left',
    },
    xAxis: {
      categories: [],
    },
    yAxis: {
      min: 0,
      title: {
        text: 'Total Percentage (%)',
      },
      stackLabels: {
        enabled: true,
      },
    },
    tooltip: {
      headerFormat: '<b>{name}</b><br/>',
      pointFormat: '{series.name}: {point.y}<br/>Total: {point.stackTotal}%',
      valueSuffix: ' %',
    },
    plotOptions: {
      column: {
        stacking: 'normal',
        dataLabels: {
          enabled: true,
        },
      },
    },
    series: [],
  };

  onLineChartInit(chart: Highcharts.Chart, form: NgForm): void {
    this.lineChartRef = chart;
    this.tryFetchCannexData(form);
  }

  onColumnChartInit(chart: Highcharts.Chart, form: NgForm): void {
    this.columnChartRef = chart;
    this.tryFetchCannexData(form);
  }

  onStackedColumnChartInit(chart: Highcharts.Chart, form: NgForm): void {
    this.stackedColumnChartRef = chart;
    this.tryFetchCannexData(form);
  }

  private clearChartRefs(): void {
    this.lineChartRef = undefined;
    this.columnChartRef = undefined;
    this.stackedColumnChartRef = undefined;
  }

  onFormValueChange(form: NgForm): void {
    this.latestForm = form;

    if (!form.valid) {
      this.clearChartRefs();
      this.refreshPending = false;
      this.indices = [];
      return;
    }

    this.tryFetchCannexData(form);
  }

  private tryFetchCannexData(form: NgForm): void {
    this.latestForm = form;
    if (
      !form.valid ||
      !this.lineChartRef ||
      !this.columnChartRef ||
      !this.stackedColumnChartRef
    ) {
      return;
    }

    if (this.requestInFlight) {
      this.refreshPending = true;
      return;
    }

    this.requestInFlight = true;
    this.refreshPending = false;
    this.loading = true;

    this.getDatafromCannexCalc()
      .pipe(
        finalize(() => {
          this.requestInFlight = false;
          this.loading = false;
          if (this.refreshPending && this.latestForm?.valid) {
            this.refreshPending = false;
            this.tryFetchCannexData(this.latestForm);
          }
        })
      )
      .subscribe((resp: CannexResponse) => {
        if (
          !this.latestForm?.valid ||
          !this.lineChartRef ||
          !this.columnChartRef ||
          !this.stackedColumnChartRef
        ) {
          return;
        }
        // Update the line chart with the new Cannex calculation results
        while (this.lineChartRef.series?.length) {
          this.lineChartRef.series[0].remove(false);
        }

        resp.lineChart.forEach((series: any) => {
          this.lineChartRef!.addSeries(
            {
              type: 'line',
              ...series,
              pointStart: Date.UTC(
                this.currentYear - (this.formModel.historicalYearsSelect ?? 0),
                0,
                1
              ),
            },
            false
          );
        });

        this.lineChartRef.redraw();
        this.lineChartRef.setTitle(
          {
            text: `${this.formModel.historicalYearsSelect}-Year Horizon Growth Graph`,
          },
          { text: `${this.formModel.historicalYearsSelect}-Year Growth` }
        );

        // Update the column chart with the new Cannex calculation results. First, remove existing series, then update the categories and add new series
        while (this.columnChartRef.series.length) {
          this.columnChartRef.series[0].remove(false);
        }
        // update the categories
        this.columnChartRef.xAxis[0].setCategories(
          resp.annuityTermOutcomeChart.categories,
          false
        );
        resp.annuityTermOutcomeChart.series.forEach((series: any) => {
          this.columnChartRef!.addSeries(series, false);
        });
        this.columnChartRef.redraw();

        // Update the stacked column chart with the new Cannex calculation results
        while (this.stackedColumnChartRef.series.length) {
          this.stackedColumnChartRef.series[0].remove(false);
        }
        this.stackedColumnChartRef.xAxis[0].setCategories(
          resp.indexPositiveTermsChart.categories,
          false
        );
        resp.indexPositiveTermsChart.series.forEach((series: any) => {
          this.stackedColumnChartRef!.addSeries(series, false);
        });
        this.stackedColumnChartRef.redraw();
        //Update table data with the new Cannex calculation results
        this.indices = resp.tableData;
      });
  }

  resetIndexPicks(): void {
    this.formModel.fiaIndexPick = [];
    this.formModel.rilaIndexPick = [];
  }

  buildCannexPayload(data: any) {
    // Build the payload for Cannex calculation based on form data to this format
    const payload = {
      initialPremium: this.mask.removeMoneyMask(data.initialPremium),
      purchaseDate: '2025-01-01', // 2025 January 1st in YYYY-MM-DD format
      timeHorizonYears: data.historicalYearsSelect,
      strategies: [],
    };
    // build the strategies array based on the IndexPick selections and the form data for FIA and RILA strategies. Each strategy should include the relevant fields based on the selected strategy type (FIA or RILA) and the corresponding form values.
    if (data.fiaStrategySelection && data.fiaIndexPick.length > 0) {
      data.fiaIndexPick.forEach((index: string) => {
        const fiaStrategy = {
          name: `${index} FIA`,
          productType: 'IndexedAnnuity',
          scenario: data.fiaStrategySelection,
          index: index,
          contractLengthYears: data.fiaContractLength,
          pointToPointTermMonths: data.fiaCalcPeriodSelect * 12,
          capRate: this.mask.removePercentMaskOnly(
            data.fiaCap ?? this.zeroPercent
          ),
          triggerRate: this.mask.removePercentMaskOnly(
            data.fiaTrigger ?? this.zeroPercent
          ),
          participationRate: this.mask.removePercentMaskOnly(
            data.fiaParticipation ?? this.zeroPercent
          ),
          minimumGuaranteedReturnPercent: this.mask.removePercentMaskOnly(
            data.minimumGuaranteedReturn ?? this.zeroPercent
          ),
          optionalAnnualFeePercent: this.mask.removePercentMaskOnly(
            data.fiaFeeValue ?? this.zeroPercent
          ),
        };
        payload.strategies.push(fiaStrategy);
      });
    }

    if (data.rilaStrategySelection && data.rilaIndexPick.length > 0) {
      data.rilaIndexPick.forEach((index: string) => {
        const rilaStrategy = {
          name: `${index} RILA`,
          productType: 'StructuredAnnuity',
          scenario: data.rilaStrategySelection,
          index: index,
          contractLengthYears: data.rilaContractLength,
          pointToPointTermMonths: data.rilaCalcPeriodSelect * 12,
          floorRate: this.mask.removePercentMaskOnly(
            data.rilaFloor ?? this.zeroPercent
          ),
          bufferRate: this.mask.removePercentMaskOnly(
            data.rilaBuffer ?? this.zeroPercent
          ),
          capRate: this.mask.removePercentMaskOnly(
            data.rilaCap ?? this.zeroPercent
          ),
          triggerRate: this.mask.removePercentMaskOnly(
            data.rilaTrigger ?? this.zeroPercent
          ),
          participationRate: this.mask.removePercentMaskOnly(
            data.rilaParticipation ?? this.zeroPercent
          ),
          optionalAnnualFeePercent: this.mask.removePercentMaskOnly(
            data.rilaFeeValue ?? this.zeroPercent
          ),
        };
        payload.strategies.push(rilaStrategy);
      });
    }

    return payload;
  }

  getDatafromCannexCalc(): Observable<any> {
    const data = this.buildCannexPayload(this.formModel);
    const token$ =
      !this.ss.get('token') || this.rbs.isTokenExpired()
        ? this.rbs.getToken().pipe(map(resp => resp.access_token))
        : of(this.ss.get('token'));

    return token$.pipe(switchMap(token => this.aws.getCannexCalc(token, data)));
  }

  calculate() {}
}
